import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { trackPageViewV2 } from "@/services/analytics-adapter";
import HealthScorePageContainer from "../HealthScorePageContainer";
import { Stack, Typography, Box } from "@mui/material";
import VitalsCategory from "../../components/VitalsCategory";
import LifestyleCategory from "../../components/LifestyleCategory";
import HealthHistoryCard from "../../components/HealthHistoryCategory";
import MentalWellbeingCategory from "../../components/MentalWellbeingCategory";
import HowItWorksModal from "../../components/HowItWorksModal";
import HealthErrorBanner from "../../components/HealthErrorBanner";
import SamsungGuidanceModal from "../../components/SamsungGuidanceModal";
import { submitMemberAcknowledgeEvent } from "@/services/core-api-adapter";
import type {
  HealthScoreDomainScore,
  HealthScoreDomainCode as HealthScoreDomain,
} from "@/services/core-api-adapter";
import { parseHealthProfileDataAsNumber } from "../../utils/parseHealthProfileData";
import { capMentalWellbeingScore } from "../../utils/capMentalWellbeingScore";
import theme from "@/theme";
import { useGlobalStore } from "@/store";
import { useTranslation } from "react-i18next";
import WellnessWheel from "../../components/WellnessWheel";
import { persistValue, retrieveValue } from "@/store/persistence";
import useGetHealthProfile from "../../hooks/useGetHealthProfile";
import useGetHealthScoreSummary from "../../hooks/useGetHealthScoreSummary";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { useNativeHealthSDK } from "@/hooks/useNativeHealthSDK";

function parseHealthScoreNumber(
  value: string | number | null | undefined
): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function getDomainScore(
  domainScores: HealthScoreDomainScore[] | undefined,
  domain: HealthScoreDomain
): HealthScoreDomainScore | undefined {
  return domainScores?.find((d) => d.domain === domain);
}

export default function HealthScorePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    error: healthError,
    isSamsungHealthInstalled,
    refreshStatus,
    openHealthConnectSettings,
  } = useNativeHealthSDK();
  const [showSamsungGuide, setShowSamsungGuide] = useState(false);
  const {
    healthProfile,
    isHealthProfileLoading,
    healthProfileFetchError,
    refetchHealthProfile,
  } = useGetHealthProfile();
  const { healthScoreSummary, refetchSummary } = useGetHealthScoreSummary();
  const initialOpenExercise = location.state?.openExercise === true;
  const initialOpenSleep = location.state?.openSleep === true;
  const pageTitle = t("HealthScorePage.title");
  const pageDescription = t("HealthscorePage.description");
  const sectionSubtitle = t("HealthScorePage.section.subtitle");
  const sectionDescription = t("HealthScorePage.section.description");
  trackPageViewV2({
    pageName: "Home:Dashboard",
    pageSubSection1: "Home",
    pageSubSection2: "Home:Dashboard",
    pageCategory: "Home",
  });

  const { state } = useGlobalStore();
  const [isModalOpen, setIsModalOpen] = React.useState(
    !state.hasSeenHealthScorePopUp || !retrieveValue("hasSeenHealthScorePopUp")
  );

  const refetch = useCallback(async () => {
    await Promise.all([refetchHealthProfile(), refetchSummary()]);
  }, [refetchHealthProfile, refetchSummary]);

  const healthScore =
    parseHealthScoreNumber(healthScoreSummary?.overallScore) ??
    parseHealthScoreNumber(healthProfile?.healthScoreValue);
  const healthScoreCompleteness = parseHealthScoreNumber(
    healthScoreSummary?.completeness
  );
  const showLoading = isHealthProfileLoading && !healthProfile;

  const domainScores = healthScoreSummary?.domainScores;
  const context = getDomainScore(domainScores, "Context");
  const vitals = getDomainScore(domainScores, "Vitals");
  const lifestyle = getDomainScore(domainScores, "Lifestyle");
  const healthHistory = getDomainScore(domainScores, "HealthHistory");
  const mental = getDomainScore(domainScores, "MentalWellbeing");

  // Business rule: if overall score is GREEN (>60) but Mental Wellbeing has
  // a RED PHQ-2 or GAD-2 (score >=3), downgrade the overall color to AMBER.
  const items = healthProfile?.items;
  const phq2Score = parseHealthProfileDataAsNumber(
    items?.find((i) => i.questionType === "Phq2Score")?.data?.value
  );
  const gad2Score = parseHealthProfileDataAsNumber(
    items?.find((i) => i.questionType === "Gad2Score")?.data?.value
  );
  const hasMentalWellbeingRed =
    (phq2Score != null && phq2Score >= 3) ||
    (gad2Score != null && gad2Score >= 3);
  const overallScoreColorOverride =
    healthScore != null && healthScore > 60 && hasMentalWellbeingRed
      ? theme.palette.secondary.main
      : undefined;

  const mentalWheelScore =
    phq2Score != null || gad2Score != null
      ? capMentalWellbeingScore(
          mental?.score ?? 0,
          gad2Score ?? 0,
          phq2Score ?? 0
        )
      : (mental?.score ?? 0);

  return (
    <React.Fragment>
      <HealthScorePageContainer
        title={pageTitle}
        description={pageDescription}
        showBottomNavigation={true}
        healthScore={healthScore}
        completion={healthScoreCompleteness}
      >
        {showLoading ? (
          <FullscreenLoadingIndicator mainIconType="face" />
        ) : (
          <Stack>
            <Stack>
              {!healthScore && (
                <Stack>
                  <Typography variant="h2">{sectionSubtitle}</Typography>
                  <Typography variant="body1">{sectionDescription}</Typography>
                </Stack>
              )}
              {!!healthScore &&
                !!healthScoreCompleteness &&
                healthScoreCompleteness > 25 && (
                  <WellnessWheel
                    score={healthScore}
                    scoreColorOverride={overallScoreColorOverride}
                    vitals={{
                      enabled: vitals !== undefined,
                      categoryScore: vitals?.score ?? 0,
                    }}
                    lifestyle={{
                      enabled: lifestyle !== undefined,
                      categoryScore: lifestyle?.score ?? 0,
                    }}
                    health={{
                      enabled: healthHistory !== undefined,
                      categoryScore: healthHistory?.score ?? 0,
                    }}
                    info={{
                      enabled: context !== undefined,
                      categoryScore: context?.score ?? 0,
                    }}
                    mentalHealth={{
                      enabled: mental !== undefined,
                      categoryScore: mentalWheelScore,
                    }}
                  />
                )}
              <HealthErrorBanner
                error={healthError}
                isSamsungDevice={isSamsungHealthInstalled}
                onRetry={refreshStatus}
                onOpenSettings={openHealthConnectSettings}
                onShowSamsungGuidance={() => setShowSamsungGuide(true)}
              />
              <Stack gap={2} mt={4}>
                <VitalsCategory
                  healthProfile={healthProfile}
                  refetch={refetch}
                  domainScore={vitals?.score}
                  domainCompleteness={vitals?.completeness}
                  domainLastUpdated={vitals?.lastDataPoint}
                />
                <LifestyleCategory
                  initialOpenExercise={initialOpenExercise}
                  initialOpenSleep={initialOpenSleep}
                  healthProfile={healthProfile}
                  refetch={refetch}
                  domainScore={lifestyle?.score}
                  domainCompleteness={lifestyle?.completeness}
                  domainLastUpdated={lifestyle?.lastDataPoint}
                />
                <HealthHistoryCard
                  healthProfile={healthProfile}
                  refetch={refetch}
                  domainScore={healthHistory?.score}
                  domainCompleteness={healthHistory?.completeness}
                  domainLastUpdated={healthHistory?.lastDataPoint}
                />
                <MentalWellbeingCategory
                  healthProfile={healthProfile}
                  refetch={refetch}
                  domainScore={mental?.score}
                  domainCompleteness={mental?.completeness}
                  domainLastUpdated={mental?.lastDataPoint}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
      </HealthScorePageContainer>
      <SamsungGuidanceModal
        isOpen={showSamsungGuide}
        onClose={() => setShowSamsungGuide(false)}
      />
      {isModalOpen && (
        <HowItWorksModal
          isOpen={isModalOpen}
          handleClose={async (isDoNotShowThisAgain) => {
            if (isDoNotShowThisAgain)
              submitMemberAcknowledgeEvent("HEALTH_SCORE_POPUP").then(() =>
                persistValue("hasSeenHealthScorePopUp", true)
              );
            setIsModalOpen(false);
          }}
        />
      )}
    </React.Fragment>
  );
}
